import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Crown, Globe, Users } from "lucide-react";

const MEDAL = ["#F5C842", "#C7D2DE", "#E08D3C"];

function Rows({ scores, emptyText }) {
  if (!scores?.length) return <p className="font-arcade text-base text-slate-600 py-2">{emptyText}</p>;
  return (
    <ol className="space-y-1.5">
      {scores.map((s, i) => (
        <li key={`${s.name}-${i}`} className="flex items-center gap-3 text-sm">
          <span className="font-arcade text-lg w-6 text-center" style={{ color: MEDAL[i] || "#64748b" }}>{i + 1}</span>
          <span className="flex-1 truncate text-slate-200">{s.name}</span>
          <span className="font-arcade text-lg text-[#00F0FF]">{s.score}</span>
        </li>
      ))}
    </ol>
  );
}

const ScopeTabs = ({ scope, setScope }) => (
  <div className="flex gap-1 mb-4" data-testid="scope-tabs">
    {[["global", "GLOBAL", Globe], ["friends", "FRIENDS", Users]].map(([val, label, Icon]) => (
      <button
        key={val}
        data-testid={`scope-tab-${val}`}
        onClick={() => setScope(val)}
        className={`flex items-center gap-1.5 font-arcade text-sm px-3 py-1.5 border transition-colors duration-200 ${scope === val ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
      >
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const GameLeaderboard = ({ gameId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("global");
  useEffect(() => {
    setData(null);
    api.get(`/leaderboard/${gameId}?limit=8&scope=${scope}`).then((r) => setData(r.data)).catch(() => setData({ scores: [] }));
  }, [gameId, refreshKey, scope]);

  return (
    <div className="bg-[#11111A] border border-cyan-500/20 p-6" data-testid={`leaderboard-${gameId}`}>
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-[#F5C842]" />
        <h3 className="font-head font-700 text-lg text-slate-50">HIGH SCORES</h3>
      </div>
      <ScopeTabs scope={scope} setScope={setScope} />
      <Rows scores={data?.scores} emptyText={scope === "friends" ? "ADD FRIENDS TO COMPETE" : "NO SCORES YET — BE THE FIRST"} />
    </div>
  );
};

export const ChampionsBanner = ({ className = "" }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/champions").then((r) => setData(r.data)).catch(() => {}); }, []);
  const anyChamp = data?.games?.some((g) => g.champion);

  return (
    <div className={`bg-gradient-to-r from-[#1a1206] to-[#11111A] border border-[#F5C842]/40 p-6 ${className}`} data-testid="champions-banner">
      <div className="flex items-center gap-2 mb-4">
        <Crown className="w-5 h-5 text-[#F5C842]" />
        <h3 className="font-head font-700 text-lg text-[#F5C842]">CHAMPIONS OF THE WEEK</h3>
        {data?.week && <span className="font-arcade text-sm text-slate-500">· {data.week}</span>}
      </div>
      {!anyChamp ? (
        <p className="font-arcade text-base text-slate-500">No champions crowned yet — top players each week win the crown when the board resets. Climb the ranks now!</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-6">
          {data.games.map((g) => (
            <div key={g.game_id}>
              <p className="font-arcade text-base text-[#FF006E] mb-1 truncate">{g.title}</p>
              {g.champion ? (
                <p className="flex items-center gap-2 text-sm text-slate-100">
                  <Crown className="w-4 h-4 text-[#F5C842]" />
                  <span className="truncate">{g.champion.name}</span>
                  <span className="font-arcade text-lg text-[#00F0FF] ml-auto">{g.champion.score}</span>
                </p>
              ) : <p className="font-arcade text-sm text-slate-600">—</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TopScores = ({ className = "" }) => {
  const [games, setGames] = useState([]);
  const [scope, setScope] = useState("global");
  useEffect(() => {
    let active = true;
    const fetchIt = () => api.get(`/leaderboard?limit=3&scope=${scope}`).then((r) => { if (active) setGames(r.data.games); }).catch(() => {});
    fetchIt();
    const id = setInterval(fetchIt, 20000);
    return () => { active = false; clearInterval(id); };
  }, [scope]);

  return (
    <div className={`bg-[#11111A] border border-cyan-500/20 p-6 ${className}`} data-testid="top-scores">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#F5C842]" />
          <h3 className="font-head font-700 text-lg text-slate-50">HALL OF FAME</h3>
          <span className="font-arcade text-sm text-slate-500">· THIS WEEK</span>
        </div>
        <ScopeTabs scope={scope} setScope={setScope} />
      </div>
      <div className="grid sm:grid-cols-3 gap-6">
        {games.map((g) => (
          <div key={g.game_id}>
            <p className="font-arcade text-base text-[#FF006E] mb-2 truncate">{g.title}</p>
            <Rows scores={g.scores} emptyText={scope === "friends" ? "ADD FRIENDS" : "NO SCORES YET"} />
          </div>
        ))}
      </div>
    </div>
  );
};
